import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { RegisteredService } from '../../../api/gen/api/registered.service';
import { ParkingZoneService } from '../../../api/gen/api/parkingZone.service';
import { ParkingSpaceService } from '../../../api/gen/api/parkingSpace.service';
import { ParkerService } from '../../../api/gen/api/parker.service';
import { OrgCostCenterService } from '../../../api/gen/api/orgCostCenter.service';
import { OrgCostCenterDto } from '../../../api/gen/model/orgCostCenterDto';
import { CreateOrgCostCenterRequest } from '../../../api/gen/model/createOrgCostCenterRequest';
import { ParkingTypeService } from '../../../api/gen/api/parkingType.service';
import { EasypassDto } from '../../../api/gen/model/easypassDto';
import { SortOrder } from '../../../api/gen/model/sortOrder';
import { environment } from '../../../environments/environment';
import { OrganizationDto } from '../../../api/gen/model/organizationDto';
import { OrganizationParkerEnrichedDto } from '../../../api/gen/model/organizationParkerEnrichedDto';
import { OrganizationMemberRole } from '../../../api/gen/model/organizationMemberRole';
import { ListOrganizationParkersRequest } from '../../../api/gen/model/listOrganizationParkersRequest';
import { AddParkingSpaceToOrganizationRequest } from '../../../api/gen/model/addParkingSpaceToOrganizationRequest';
import { RemoveParkingSpaceFromOrganizationRequest } from '../../../api/gen/model/removeParkingSpaceFromOrganizationRequest';
import { AddParkerToOrganizationRequest } from '../../../api/gen/model/addParkerToOrganizationRequest';
import { RemoveParkerFromOrganizationRequest } from '../../../api/gen/model/removeParkerFromOrganizationRequest';
import { RegisterLicensePlateRequest } from '../../../api/gen/model/registerLicensePlateRequest';
import { AddParkingZoneToOrganizationRequest } from '../../../api/gen/model/addParkingZoneToOrganizationRequest';
import { RemoveParkingZoneFromOrganizationRequest } from '../../../api/gen/model/removeParkingZoneFromOrganizationRequest';
import { ParkingZoneDto } from '../../../api/gen/model/parkingZoneDto';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';
import { RegisteredParkerDto } from '../../../api/gen/model/registeredParkerDto';
import { CreatedRegisteredParkerRequest } from '../../../api/gen/model/createdRegisteredParkerRequest';
import { ParkingProcessService } from '../../../api/gen/api/parkingProcess.service';
import { ParkingProcessListItemDto } from '../../../api/gen/model/parkingProcessListItemDto';
import { EasypassProcessListItemDto } from '../../../api/gen/model/easypassProcessListItemDto';
import { ListOrganizationEasypassProcessesRequest } from '../../../api/gen/model/listOrganizationEasypassProcessesRequest';
import {
    JjamesButtonComponent, JJamesAlertComponent, JJamesPaginatorComponent, JJamesPaginationConfig,
    JJamesTimelineChartComponent, TimelineParkingEvent,
    ResponsiveBaseComponent, countryName, toDateInputValue
} from 'shared-lib';
import { ChangeMemberRoleDialogComponent, ChangeMemberRoleDialogData } from './change-member-role-dialog/change-member-role-dialog.component';
import { ManageMemberPlatesDialogComponent, ManageMemberPlatesDialogData } from './manage-member-plates-dialog/manage-member-plates-dialog.component';
import { ListRegisteredParkersRequest } from '../../../api/gen';

/**
 * One Easypass stay, flattened with the invitation that admitted it. Both come from the org-wide
 * transactions endpoint: a stay knows only its ParkingRight, and each redemption mints its own, so
 * the server attaches the invitation's title and window to each row rather than the template looking
 * them up again.
 */
interface EasypassStayRow {
    processId: string;
    licensePlate: string;
    startedAt: string;
    endedAt?: string | null;
    invitationTitle?: string | null;
    invitationValidFrom?: string | null;
    invitationValidTo?: string | null;
}

@Component({
    selector: 'app-org-view',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatSnackBarModule,
        MatIconModule,
        MatTooltipModule,
        JjamesButtonComponent,
        JJamesAlertComponent,
        JJamesPaginatorComponent,
        JJamesTimelineChartComponent,
        TranslatePipe
    ],
    templateUrl: './org-view.component.html',
    styleUrls: ['./org-view.component.scss']
})
export class OrgViewComponent extends ResponsiveBaseComponent implements OnInit {
    private readonly orgService = inject(OrganizationService);
    private readonly registeredService = inject(RegisteredService);
    private readonly parkingZoneService = inject(ParkingZoneService);
    private readonly parkingSpaceService = inject(ParkingSpaceService);
    private readonly parkerService = inject(ParkerService);
    private readonly parkingProcessService = inject(ParkingProcessService);
    private readonly orgCostCenterService = inject(OrgCostCenterService);
    private readonly parkingTypeService = inject(ParkingTypeService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);
    private readonly snackBar = inject(MatSnackBar);
    private readonly translate = inject(TranslateService);

    readonly Role = OrganizationMemberRole;

    organization = signal<OrganizationDto | null>(null);
    loading = signal(false);
    errorMessage = signal<string | null>(null);
    successMessage = signal<string | null>(null);
    // Consolidated workspaces: Profile & Billing / Transactions / Parkers & Access / Parking Rules & Inventory / Easypasses.
    activeTab = signal<'profile' | 'transactions' | 'parkers' | 'inventory' | 'easypasses' | 'easypassTransactions'>('profile');

    // ── Transactions workspace ────────────────────────────────────────────────
    // An organization's stays are the union over its pool configs: a process records the pool
    // it used and never the org, so there is nothing else to key on. The backend filter reads
    // FallbackFromPoolConfigId too, so overflow stays are included.
    transactions = signal<ParkingProcessListItemDto[]>([]);
    transactionsLoading = signal(false);
    transactionsError = signal<string | null>(null);
    transactionsLoaded = signal(false);
    txFrom = signal('');
    txTo = signal('');
    txPageIndex = signal(0);
    txPageSize = signal(20);
    txTotalCount = signal(0);

    // The table is server-paged, so the rows in `transactions` are one page. The chart needs the
    // whole window or it would draw occupancy from twenty stays and call it a month, so it gets its
    // own unpaged read. Capped, with the cap surfaced rather than silently truncating the picture.
    private static readonly TimelineFetchCap = 500;
    txTimelineEvents = signal<TimelineParkingEvent[]>([]);
    txTimelineTruncated = signal(false);
    /** Space names for the ids appearing in the loaded rows — the org link runs through pools, so the
     *  directly-linked spaces map is not enough to name every space a stay can land in. */
    txSpaceNames = signal<Map<string, string>>(new Map());

    /** Total paid capacity across the org's pools — the line the chart marks as full. */
    readonly txCapacity = computed(() =>
        (this.organization()?.poolParkConfigs ?? [])
            .reduce((sum, c) => sum + (c.maxCarsAllowed ?? 0), 0));
    orgId: string | null = null;
    deleteConfirmId = signal<string | null>(null);
    parkersLoading = signal(false);
    // One page of enriched members. The organization fetch no longer carries them: enriching every
    // member of a large organization on each load was the slow part of opening this page.
    members = signal<OrganizationParkerEnrichedDto[]>([]);
    membersLoaded = signal(false);
    membersPageIndex = signal(0);
    membersPageSize = signal(20);
    membersTotalCount = signal(0);
    private membersSub?: Subscription;
    spacesLoading = signal(false);
    zoneNames = signal<Map<string, string>>(new Map());
    spaceNames = signal<Map<string, string>>(new Map());
    zoneNamesLoading = signal(false);
    spaceNamesLoading = signal(false);
    showAddSpaceForm = signal(false);
    /** Free-text narrowing of the loaded list — not a server search. */
    addSpaceSearchName = signal('');
    /** Every space this operator may see, loaded once when the add form opens. */
    accessibleSpaces = signal<ParkingSpaceDto[]>([]);
    addSpaceSearchLoading = signal(false);
    addSpaceError = signal<string | null>(null);
    deleteSpaceConfirmId = signal<string | null>(null);
    showAddParkerForm = signal(false);
    addParkerMode = signal<'lp' | 'email'>('lp');
    addParkerRole = signal<OrganizationMemberRole>(OrganizationMemberRole.Member);
    addParkerError = signal<string | null>(null);
    addParkerLoading = signal(false);
    deleteParkerConfirmId = signal<string | null>(null);
    parkerRoleFilter = signal<'all' | 'admins' | 'members'>('all');
    /** Name, email or plate; searched server-side across every member, not just the loaded page. */
    membersSearch = signal('');
    private membersSearchTimer?: ReturnType<typeof setTimeout>;
    // LP mode
    addParkerLpInput = signal('');
    // Email mode
    addParkerEmailInput = signal('');
    addParkerEmailResults = signal<RegisteredParkerDto[]>([]);
    addParkerEmailSearchDone = signal(false);
    // Create parker sub-form (shown when email search yields no results)
    showCreateParkerForm = signal(false);
    createParkerFirstName = signal('');
    createParkerLastName = signal('');
    createParkerPassword = signal('');
    createParkerLp = signal('');
    showAddZoneForm = signal(false);
    addZoneSpaceId = signal('');
    addZoneSpaceZones = signal<ParkingZoneDto[]>([]);
    addZoneSelectedIds = signal<Set<string>>(new Set());
    addZoneLoadingZones = signal(false);
    addZoneError = signal<string | null>(null);
    addZoneSubmitting = signal(false);
    deleteZoneConfirmId = signal<string | null>(null);

    // ── Cost Centers ──────────────────────────────────────────────────────────
    /** This organization's own cost centers only — global rows are managed elsewhere, not here. */
    costCenters = signal<OrgCostCenterDto[]>([]);
    costCentersLoading = signal(false);
    showAddCostCenterForm = signal(false);
    newCostCenterName = signal('');
    newCostCenterDescription = signal('');
    addCostCenterError = signal<string | null>(null);
    addCostCenterSubmitting = signal(false);
    deleteCostCenterConfirmId = signal<string | null>(null);

    // ── Easypass Invitations ─────────────────────────────────────────────────
    easypasses = signal<EasypassDto[]>([]);
    easypassesLoading = signal(false);
    easypassesLoaded = signal(false);
    easypassesError = signal<string | null>(null);
    easypassesTotalCount = signal(0);
    ezPageIndex = signal(0);
    ezPageSize = signal(20);
    revokeEasypassConfirmId = signal<string | null>(null);

    // ── Easypass Transactions ────────────────────────────────────────────────
    ezTxRows = signal<EasypassStayRow[]>([]);
    ezTxLoading = signal(false);
    ezTxLoaded = signal(false);
    ezTxError = signal<string | null>(null);
    ezTxPageIndex = signal(0);
    ezTxPageSize = signal(20);
    ezTxTotalCount = signal(0);

    ngOnInit(): void {
        this.orgId = this.route.snapshot.paramMap.get('orgId');
        if (this.orgId) {
            this.loadOrganization();
        } else {
            this.errorMessage.set(this.translate.instant('organizationView.errors.noOrgId'));
        }
    }

    loadOrganization(): void {
        if (!this.orgId) return;

        this.loading.set(true);
        this.errorMessage.set(null);

        this.orgService.organizationsOrganizationIdFetchPost(this.orgId, {
            includeDeleted: false,
            includeValidity: true,
            includeMembers: false
        }).subscribe({
            next: (response) => {
                if (response) {
                    this.organization.set(response);
                    // Membership may have just changed (add / remove / role); refresh the visible page.
                    if (this.membersLoaded()) {
                        this.loadMembers();
                    }
                    this.loadZoneNames();
                    this.loadSpaceNames();
                    this.loadCostCenters();
                }
                this.loading.set(false);
            },
            error: (error) => {
                console.error('Error loading organization:', error);
                this.errorMessage.set(this.translate.instant('organizationView.errors.loadFailed'));
                this.loading.set(false);
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/org-management']);
    }

    editOrganization(): void {
        if (this.orgId) {
            this.router.navigate(['/org-management', this.orgId, 'edit']);
        }
    }

    setTab(tab: 'profile' | 'transactions' | 'parkers' | 'inventory' | 'easypasses' | 'easypassTransactions'): void {
        this.activeTab.set(tab);
        // The inventory workspace shows zones + spaces together, so resolve both name maps.
        if (tab === 'inventory') {
            this.loadZoneNames();
            this.loadSpaceNames();
        }
        // Loaded on first visit only — this is the one tab that costs a query, and re-fetching on
        // every tab click would throw away the operator's date filter and page position.
        if (tab === 'transactions' && !this.transactionsLoaded()) {
            this.initTransactionFilter();
            this.loadTransactions();
            this.loadTransactionTimeline();
        }
        if (tab === 'parkers' && !this.membersLoaded()) {
            this.loadMembers();
        }
        if (tab === 'easypasses' && !this.easypassesLoaded()) {
            this.loadEasypasses();
        }
        if (tab === 'easypassTransactions' && !this.ezTxLoaded()) {
            this.loadEasypassTransactions();
        }
    }

    /**
     * Lists every stay parked under this organization's Easypass invitations.
     *
     * One server call: the org-wide endpoint resolves the organization's invitations itself and
     * returns the merged, sorted, paged stays. The old implementation fetched the invitations and
     * then issued one stay query per invitation — hundreds of round trips for a large organization.
     * The read stays scoped by organizationId, so a row can only come from an invitation this
     * organization owns.
     */
    loadEasypassTransactions(): void {
        if (!this.orgId) return;
        const orgId = this.orgId;

        this.ezTxLoading.set(true);
        this.ezTxError.set(null);

        const request: ListOrganizationEasypassProcessesRequest = {
            organizationId: orgId,
            pageIndex: this.ezTxPageIndex(),
            pageSize: this.ezTxPageSize(),
            sortBy: 'StartedAt',
            sortOrder: SortOrder.Descending,
            includeDeleted: false
        };

        this.parkingProcessService.parkingProcessEasypassProcessesPost(request).subscribe({
            next: (result) => {
                this.ezTxRows.set((result.items ?? []).map(item => this.toEasypassStayRow(item)));
                this.ezTxTotalCount.set(result.totalCount ?? 0);
                this.ezTxLoading.set(false);
                this.ezTxLoaded.set(true);
            },
            error: (error) => {
                console.error('Error loading Easypass transactions:', error);
                this.ezTxError.set(this.translate.instant('organizationView.easypassTransactions.loadFailed'));
                this.ezTxLoading.set(false);
            }
        });
    }

    /** Flattens one server row — a stay plus the invitation that admitted it — onto the table row. */
    private toEasypassStayRow(item: EasypassProcessListItemDto): EasypassStayRow {
        const process = item.process;
        return {
            processId: process?.id ?? '',
            licensePlate: process?.licensePlateDetails?.fullNumber ?? '—',
            startedAt: process?.startedAt ?? '',
            endedAt: process?.endedAt,
            invitationTitle: item.easypassTitle,
            invitationValidFrom: item.easypassValidFrom,
            invitationValidTo: item.easypassValidTo
        };
    }

    onEasypassTransactionsPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.ezTxPageIndex.set(event.pageIndex);
        this.ezTxPageSize.set(event.pageSize);
        this.loadEasypassTransactions();
    }

    get easypassTransactionsPagination(): JJamesPaginationConfig {
        const total = this.ezTxTotalCount();
        const size = this.ezTxPageSize();
        return {
            currentPage: this.ezTxPageIndex(),
            totalPages: Math.max(1, Math.ceil(total / size)),
            pageSize: size,
            totalItems: total,
            itemsPerPage: size,
            pageSizeOptions: [10, 20, 50]
        };
    }

    /** True when the billing address has any populated field — drives the empty-state placeholder. */
    get hasBillingAddress(): boolean {
        const b = this.organization()?.billingAddress;
        return !!(b && (b.firstAddressLine || b.secondAddressLine || b.locality || b.postCode || b.region || b.countryCode));
    }

    /** Country name for a stored ISO code; the raw code for one the list does not cover yet. */
    countryLabel(code: string | null | undefined): string {
        return countryName(code) || '—';
    }

    getEnrichedParkers(): OrganizationParkerEnrichedDto[] {
        return this.members();
    }

    /** Loads the current members page; a newer request cancels the one still in flight. */
    loadMembers(): void {
        if (!this.orgId) return;
        const filter = this.parkerRoleFilter();
        const request: ListOrganizationParkersRequest = {
            role: filter === 'admins' ? OrganizationMemberRole.Admin
                : filter === 'members' ? OrganizationMemberRole.Member
                : undefined,
            search: this.membersSearch().trim() || undefined,
            pageIndex: this.membersPageIndex(),
            pageSize: this.membersPageSize(),
            sortBy: 'CreatedAt',
            sortOrder: SortOrder.Descending
        };

        this.membersSub?.unsubscribe();
        this.parkersLoading.set(true);
        this.membersSub = this.orgService.organizationsOrganizationIdParkersFilterPost(this.orgId, request).subscribe({
            next: (result) => {
                this.members.set(result?.items ?? []);
                this.membersTotalCount.set(result?.totalCount ?? 0);
                this.membersLoaded.set(true);
                this.parkersLoading.set(false);
            },
            error: (error) => {
                console.error('Error loading organization members:', error);
                this.members.set([]);
                this.membersTotalCount.set(0);
                this.errorMessage.set(this.translate.instant('organizationView.errors.loadFailed'));
                this.parkersLoading.set(false);
            }
        });
    }

    setParkerRoleFilter(filter: 'all' | 'admins' | 'members'): void {
        if (this.parkerRoleFilter() === filter) return;
        this.parkerRoleFilter.set(filter);
        this.membersPageIndex.set(0);
        this.loadMembers();
    }

    /** Debounced, and ignores 1-character terms, so typing does not query per keystroke. */
    onMembersSearchInput(value: string): void {
        this.membersSearch.set(value);
        clearTimeout(this.membersSearchTimer);
        const term = value.trim();
        if (term.length === 1) return;
        this.membersSearchTimer = setTimeout(() => {
            this.membersPageIndex.set(0);
            this.loadMembers();
        }, 500);
    }

    onMembersPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.membersPageIndex.set(event.pageIndex);
        this.membersPageSize.set(event.pageSize);
        this.loadMembers();
    }

    get membersPagination(): JJamesPaginationConfig {
        const total = this.membersTotalCount();
        const size = this.membersPageSize();
        return {
            currentPage: this.membersPageIndex(),
            totalPages: Math.max(1, Math.ceil(total / size)),
            pageSize: size,
            totalItems: total,
            itemsPerPage: size,
            pageSizeOptions: [10, 20, 50]
        };
    }

    getParkerInitials(p: OrganizationParkerEnrichedDto): string {
        const f = p.firstName?.[0] ?? '';
        const l = p.lastName?.[0] ?? '';
        return (f + l).toUpperCase() || '?';
    }

    countAdmins(): number {
        return (this.organization()?.parkers ?? []).filter(p => p.role === OrganizationMemberRole.Admin).length;
    }

    isOnlyAdmin(parkerId: string): boolean {
        const parker = (this.organization()?.parkers ?? []).find(p => p.parkerId === parkerId);
        return parker?.role === OrganizationMemberRole.Admin && this.countAdmins() === 1;
    }

    openChangeRoleDialog(parker: OrganizationParkerEnrichedDto): void {
        if (!this.orgId) return;
        if (this.isOnlyAdmin(parker.parkerId!)) {
            this.errorMessage.set(this.translate.instant('organizationView.errors.lastAdmin'));
            return;
        }
        const data: ChangeMemberRoleDialogData = {
            organizationId: this.orgId,
            parker
        };
        const ref = this.dialog.open(ChangeMemberRoleDialogComponent, { data, width: '420px' });
        ref.afterClosed().subscribe(changed => {
            if (changed) this.loadOrganization();
        });
    }

    /**
     * Opens the plate manager for one member.
     *
     * Same rules as the parker app's own "My License Plates" — the plate an operator adds here has
     * to normalize to exactly what the parker would have produced, or the two spellings of the same
     * car are two different rows and the member is denied at the gate.
     */
    openManagePlatesDialog(parker: OrganizationParkerEnrichedDto): void {
        if (!parker.parkerId) return;
        const data: ManageMemberPlatesDialogData = {
            parkerId: parker.parkerId,
            displayName: this.getParkerDisplayName(parker),
            // Members without an account are GuestParkers, and the dialog's endpoints differ per
            // family — without this the guest's plates 404 on open.
            parkerType: parker.parkerType
        };
        const ref = this.dialog.open(ManageMemberPlatesDialogComponent, {
            data,
            width: this.isHandset() ? '95vw' : '760px',
            maxWidth: this.isHandset() ? '95vw' : '760px'
        });
        // Only reload when something actually changed: the member table renders activeLicenses
        // straight off the organization payload, so a plain Close must not cost a round trip.
        ref.afterClosed().subscribe(changed => {
            if (changed) this.loadOrganization();
        });
    }

    getParkerDisplayName(parker: OrganizationParkerEnrichedDto): string {
        const name = `${parker.firstName ?? ''} ${parker.lastName ?? ''}`.trim();
        return name || parker.email || parker.parkerId || this.translate.instant('organizationView.parkers.thisMember');
    }

    loadZoneNames(): void {
        const zones = this.organization()?.parkingZones;
        if (!zones?.length) return;
        const ids = zones.map(z => z.parkingZoneId).filter((id): id is string => !!id);
        if (!ids.length) return;
        const existing = this.zoneNames();
        if (ids.every(id => existing.has(id))) return;
        this.zoneNamesLoading.set(true);
        const requests = ids.map(id =>
            this.parkingZoneService.parkingZoneParkingZoneIdGet(id).pipe(catchError(() => of(null)))
        );
        forkJoin(requests).subscribe(results => {
            const map = new Map(existing);
            results.forEach((dto, i) => { if (dto) map.set(ids[i], dto.name); });
            this.zoneNames.set(map);
            this.zoneNamesLoading.set(false);
        });
    }

    loadSpaceNames(): void {
        const spaces = this.organization()?.parkingSpaces;
        if (!spaces?.length) return;
        const ids = spaces.map(s => s.parkingSpaceId).filter((id): id is string => !!id);
        if (!ids.length) return;
        const existing = this.spaceNames();
        if (ids.every(id => existing.has(id))) return;
        this.spaceNamesLoading.set(true);
        const requests = ids.map(id =>
            this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(id).pipe(catchError(() => of(null)))
        );
        forkJoin(requests).subscribe(results => {
            const map = new Map(existing);
            results.forEach((dto, i) => { if (dto) map.set(ids[i], dto.name ?? ids[i]); });
            this.spaceNames.set(map);
            this.spaceNamesLoading.set(false);
        });
    }

    getZoneName(zoneId: string): string {
        return this.zoneNames().get(zoneId) ?? zoneId;
    }

    getSpaceName(spaceId: string): string {
        return this.spaceNames().get(spaceId) ?? spaceId;
    }

    addPoolConfig(): void {
        if (this.orgId) {
            this.router.navigate(['/org-management', this.orgId, 'pool-config', 'new']);
        }
    }

    editPoolConfig(configId: string): void {
        if (this.orgId) {
            this.router.navigate(['/org-management', this.orgId, 'pool-config', configId]);
        }
    }

    confirmDeletePoolConfig(configId: string): void {
        this.deleteConfirmId.set(configId);
    }

    cancelDelete(): void {
        this.deleteConfirmId.set(null);
    }

    deletePoolConfig(configId: string): void {
        if (!this.orgId) return;

        this.orgService.organizationsOrganizationIdPoolParkingConfigsConfigIdDelete(this.orgId, configId).subscribe({
            next: () => {
                this.successMessage.set(this.translate.instant('organizationView.success.poolConfigRemoved'));
                this.deleteConfirmId.set(null);
                this.loadOrganization();
            },
            error: (error: any) => {
                console.error('Error removing pool config:', error);
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.removePoolConfig'));
                this.deleteConfirmId.set(null);
            }
        });
    }

    deleteOrganization(): void {
        if (!this.orgId) return;

        this.orgService.organizationsOrganizationIdDelete(this.orgId, {}).subscribe({
            next: () => {
                this.router.navigate(['/org-management']);
            },
            error: (error) => {
                console.error('Error deleting organization:', error);
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.deleteOrganization'));
            }
        });
    }

    /**
     * Opens the add-space form and loads the spaces this operator may assign.
     *
     * The list comes from the space filter endpoint, which is already scoped server-side to the
     * caller's operator grants, so what lands here is exactly "spaces I can access" — an operator
     * can no longer type the name of a colleague's site and attach it to an organization.
     */
    openAddSpaceForm(): void {
        this.showAddSpaceForm.set(true);
        this.addSpaceError.set(null);
        this.addSpaceSearchName.set('');
        if (this.accessibleSpaces().length > 0) return;

        this.addSpaceSearchLoading.set(true);
        // Page size 100 is the endpoint's maximum; the name box below filters the loaded list.
        this.parkingSpaceService.parkingSpaceFilterPost(
            undefined, undefined, undefined, undefined, undefined, undefined, 0, 100, 'Name')
            .subscribe({
                next: (result) => {
                    this.accessibleSpaces.set(result.items ?? []);
                    this.addSpaceSearchLoading.set(false);
                    if (!result.items?.length) {
                        this.addSpaceError.set(this.translate.instant('organizationView.addSpace.noAccess'));
                    }
                },
                error: () => {
                    this.addSpaceError.set(this.translate.instant('organizationView.addSpace.loadFailed'));
                    this.addSpaceSearchLoading.set(false);
                }
            });
    }

    cancelAddSpace(): void {
        this.showAddSpaceForm.set(false);
        this.addSpaceSearchName.set('');
        this.addSpaceError.set(null);
    }

    /** The accessible spaces narrowed by the name box, minus the ones already on this org. */
    getAddSpaceOptions(): ParkingSpaceDto[] {
        const term = this.addSpaceSearchName().trim().toLowerCase();
        const assigned = new Set((this.organization()?.parkingSpaces ?? []).map(s => s.parkingSpaceId));
        return this.accessibleSpaces().filter(space =>
            !assigned.has(space.id) &&
            (!term || (space.name ?? '').toLowerCase().includes(term)));
    }

    addParkingSpace(spaceId: string): void {
        if (!spaceId || !this.orgId) return;
        this.addSpaceError.set(null);
        this.spacesLoading.set(true);
        const req: AddParkingSpaceToOrganizationRequest = { parkingSpaceId: spaceId };
        this.orgService.organizationsOrganizationIdParkingSpacesPost(this.orgId, req).subscribe({
            next: () => {
                this.addSpaceSearchName.set('');
                this.showAddSpaceForm.set(false);
                this.spacesLoading.set(false);
                this.successMessage.set(this.translate.instant('organizationView.success.spaceAdded'));
                this.loadOrganization();
            },
            error: (error: any) => {
                this.addSpaceError.set(error.error?.title || this.translate.instant('organizationView.errors.addSpace'));
                this.spacesLoading.set(false);
            }
        });
    }

    confirmRemoveSpace(spaceId: string): void {
        this.deleteSpaceConfirmId.set(spaceId);
    }

    cancelRemoveSpace(): void {
        this.deleteSpaceConfirmId.set(null);
    }

    removeParkingSpace(spaceId: string): void {
        if (!this.orgId) return;
        this.spacesLoading.set(true);
        const req: RemoveParkingSpaceFromOrganizationRequest = { parkingSpaceId: spaceId };
        this.orgService.organizationsOrganizationIdParkingSpacesDelete(this.orgId, req).subscribe({
            next: () => {
                this.deleteSpaceConfirmId.set(null);
                this.spacesLoading.set(false);
                this.successMessage.set(this.translate.instant('organizationView.success.spaceRemoved'));
                this.loadOrganization();
            },
            error: (error: any) => {
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.removeSpace'));
                this.deleteSpaceConfirmId.set(null);
                this.spacesLoading.set(false);
            }
        });
    }

    addParkerByLp(): void {
        const lp = this.addParkerLpInput().trim().toUpperCase();
        if (!lp || !this.orgId) {
            this.addParkerError.set(this.translate.instant('organizationView.addParker.enterPlate'));
            return;
        }
        this.addParkerError.set(null);
        this.addParkerLoading.set(true);
        const regReq: RegisterLicensePlateRequest = { fullNumber: lp };
        this.parkerService.parkerRegisterLicensePlatePost(regReq).subscribe({
            next: (lpDto) => {
                const parkerId = lpDto.parkersIDs?.at(0);
                if (!parkerId) {
                    this.addParkerError.set(this.translate.instant('organizationView.addParker.noParkerForPlate'));
                    this.addParkerLoading.set(false);
                    return;
                }
                this.doAddParkerToOrg(parkerId, lpDto.fullNumber ?? lp);
            },
            error: (error: any) => {
                this.addParkerError.set(error.error?.title || this.translate.instant('organizationView.errors.registerPlate'));
                this.addParkerLoading.set(false);
            }
        });
    }

    searchParkerByEmail(): void {
        const email = this.addParkerEmailInput().trim();
        if (!email) {
            this.addParkerError.set(this.translate.instant('organizationView.addParker.enterEmail'));
            return;
        }
        this.addParkerError.set(null);
        this.addParkerLoading.set(true);
        this.addParkerEmailResults.set([]);
        this.addParkerEmailSearchDone.set(false);
        this.showCreateParkerForm.set(false);
        var request: ListRegisteredParkersRequest = {
            email,
            pageSize: 5,
            pageIndex: 0
        };
        this.registeredService.operatorRegisteredParkerFilterPost(request).subscribe({
            next: (result) => {
                this.addParkerEmailResults.set(result.items ?? []);
                this.addParkerEmailSearchDone.set(true);
                this.addParkerLoading.set(false);
            },
            error: (error: any) => {
                this.addParkerError.set(error.error?.title || this.translate.instant('organizationView.errors.searchFailed'));
                this.addParkerLoading.set(false);
            }
        });
    }

    addParkerByEmailResult(parker: RegisteredParkerDto): void {
        if (!parker.id || !this.orgId) return;
        this.addParkerError.set(null);
        this.addParkerLoading.set(true);
        this.doAddParkerToOrg(parker.id, `${parker.firstName ?? ''} ${parker.lastName ?? ''}`.trim() || parker.email || parker.id);
    }

    createAndAddParker(): void {
        const email = this.addParkerEmailInput().trim();
        const firstName = this.createParkerFirstName().trim();
        const lastName = this.createParkerLastName().trim();
        const password = this.createParkerPassword().trim();
        if (!email || !firstName || !lastName || !password) {
            this.addParkerError.set(this.translate.instant('organizationView.addParker.requiredFields'));
            return;
        }
        this.addParkerError.set(null);
        this.addParkerLoading.set(true);
        const req: CreatedRegisteredParkerRequest = { email, firstName, lastName, defaultPassword: password };
        this.registeredService.operatorRegisteredParkerPost(req).subscribe({
            next: (created) => {
                const parkerId = created.id;
                if (!parkerId) {
                    this.addParkerError.set(this.translate.instant('organizationView.addParker.missingId'));
                    this.addParkerLoading.set(false);
                    return;
                }
                const lp = this.createParkerLp().trim().toUpperCase();
                if (lp) {
                    this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: lp }).subscribe({
                        next: () => this.doAddParkerToOrg(parkerId, `${firstName} ${lastName}`),
                        error: () => this.doAddParkerToOrg(parkerId, `${firstName} ${lastName}`)
                    });
                } else {
                    this.doAddParkerToOrg(parkerId, `${firstName} ${lastName}`);
                }
            },
            error: (error: any) => {
                this.addParkerError.set(error.error?.title || this.translate.instant('organizationView.errors.createParker'));
                this.addParkerLoading.set(false);
            }
        });
    }

    private doAddParkerToOrg(parkerId: string, label: string): void {
        const addReq: AddParkerToOrganizationRequest = { parkerId, role: this.addParkerRole() };
        this.orgService.organizationsOrganizationIdParkersPost(this.orgId!, addReq).subscribe({
            next: () => {
                this.resetAddParkerForm();
                this.successMessage.set(this.translate.instant('organizationView.success.parkerAdded', { label }));
                this.loadOrganization();
            },
            error: (error: any) => {
                this.addParkerError.set(error.error?.title || this.translate.instant('organizationView.errors.addParker'));
                this.addParkerLoading.set(false);
            }
        });
    }

    resetAddParkerForm(): void {
        this.addParkerLpInput.set('');
        this.addParkerEmailInput.set('');
        this.addParkerEmailResults.set([]);
        this.addParkerEmailSearchDone.set(false);
        this.showCreateParkerForm.set(false);
        this.createParkerFirstName.set('');
        this.createParkerLastName.set('');
        this.createParkerPassword.set('');
        this.createParkerLp.set('');
        this.addParkerRole.set(OrganizationMemberRole.Member);
        this.showAddParkerForm.set(false);
        this.addParkerLoading.set(false);
        this.addParkerError.set(null);
    }

    confirmRemoveParker(parkerId: string): void {
        if (this.isOnlyAdmin(parkerId)) {
            this.errorMessage.set(this.translate.instant('organizationView.errors.lastAdmin'));
            return;
        }
        this.deleteParkerConfirmId.set(parkerId);
    }

    cancelRemoveParker(): void {
        this.deleteParkerConfirmId.set(null);
    }

    removeParker(parkerId: string): void {
        if (!this.orgId) return;
        this.addParkerLoading.set(true);
        const req: RemoveParkerFromOrganizationRequest = { parkerId };
        this.orgService.organizationsOrganizationIdParkersDelete(this.orgId, req).subscribe({
            next: () => {
                this.deleteParkerConfirmId.set(null);
                this.addParkerLoading.set(false);
                this.successMessage.set(this.translate.instant('organizationView.success.parkerRemoved'));
                this.loadOrganization();
            },
            error: (error: any) => {
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.removeParker'));
                this.deleteParkerConfirmId.set(null);
                this.addParkerLoading.set(false);
            }
        });
    }

    cancelAddZone(): void {
        this.showAddZoneForm.set(false);
        this.addZoneSpaceId.set('');
        this.addZoneSpaceZones.set([]);
        this.addZoneSelectedIds.set(new Set());
        this.addZoneError.set(null);
    }

    onAddZoneSpaceChange(spaceId: string): void {
        this.addZoneSpaceId.set(spaceId);
        this.addZoneSelectedIds.set(new Set());
        this.addZoneSpaceZones.set([]);
        this.addZoneError.set(null);
        if (!spaceId) return;
        this.addZoneLoadingZones.set(true);
        this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(spaceId).subscribe({
            next: (dto) => {
                this.addZoneSpaceZones.set(dto.parkingZones ?? []);
                this.addZoneLoadingZones.set(false);
            },
            error: () => {
                this.addZoneError.set(this.translate.instant('organizationView.addZone.loadFailed'));
                this.addZoneLoadingZones.set(false);
            }
        });
    }

    toggleZoneSelection(zoneId: string): void {
        const current = new Set(this.addZoneSelectedIds());
        if (current.has(zoneId)) {
            current.delete(zoneId);
        } else {
            current.add(zoneId);
        }
        this.addZoneSelectedIds.set(current);
    }

    isZoneSelected(zoneId: string): boolean {
        return this.addZoneSelectedIds().has(zoneId);
    }

    isZoneAlreadyAssigned(zoneId: string): boolean {
        return !!this.organization()?.parkingZones?.some(z => z.parkingZoneId === zoneId);
    }

    addZonesToOrg(): void {
        const selectedIds = Array.from(this.addZoneSelectedIds());
        if (!selectedIds.length || !this.orgId) {
            this.addZoneError.set(this.translate.instant('organizationView.addZone.selectZone'));
            return;
        }
        this.addZoneError.set(null);
        this.addZoneSubmitting.set(true);
        const requests = selectedIds.map(zoneId => {
            const req: AddParkingZoneToOrganizationRequest = { parkingZoneId: zoneId };
            return this.orgService.organizationsOrganizationIdParkingZonesPost(this.orgId!, req).pipe(
                catchError(() => of(null))
            );
        });
        forkJoin(requests).subscribe(() => {
            this.showAddZoneForm.set(false);
            this.addZoneSpaceId.set('');
            this.addZoneSpaceZones.set([]);
            this.addZoneSelectedIds.set(new Set());
            this.addZoneSubmitting.set(false);
            this.successMessage.set(this.translate.instant('organizationView.success.zonesAdded', { count: selectedIds.length }));
            this.loadOrganization();
        });
    }

    confirmRemoveZone(zoneId: string): void {
        this.deleteZoneConfirmId.set(zoneId);
    }

    cancelRemoveZone(): void {
        this.deleteZoneConfirmId.set(null);
    }

    removeZone(zoneId: string): void {
        if (!this.orgId) return;
        const req: RemoveParkingZoneFromOrganizationRequest = { parkingZoneId: zoneId };
        this.orgService.organizationsOrganizationIdParkingZonesDelete(this.orgId, req).subscribe({
            next: () => {
                this.deleteZoneConfirmId.set(null);
                this.successMessage.set(this.translate.instant('organizationView.success.zoneRemoved'));
                this.loadOrganization();
            },
            error: (error: any) => {
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.removeZone'));
                this.deleteZoneConfirmId.set(null);
            }
        });
    }

    // ── Cost Centers ──────────────────────────────────────────────────────────

    /**
     * Loads cost centers usable for this org (its own rows plus every global one, per the backend
     * query), then narrows to this org's own — global rows are shown to pickers elsewhere but are
     * not this org's to manage from here.
     */
    loadCostCenters(): void {
        if (!this.orgId) return;
        this.costCentersLoading.set(true);
        this.orgCostCenterService.orgCostCentersGet(this.orgId).subscribe({
            next: (result) => {
                this.costCenters.set((result ?? []).filter(c => c.organizationId === this.orgId));
                this.costCentersLoading.set(false);
            },
            error: (error: any) => {
                console.error('Error loading cost centers:', error);
                this.costCenters.set([]);
                this.costCentersLoading.set(false);
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.loadCostCenters'));
            }
        });
    }

    openAddCostCenterForm(): void {
        this.showAddCostCenterForm.set(true);
        this.addCostCenterError.set(null);
        this.newCostCenterName.set('');
        this.newCostCenterDescription.set('');
    }

    cancelAddCostCenter(): void {
        this.showAddCostCenterForm.set(false);
        this.addCostCenterError.set(null);
    }

    addCostCenter(): void {
        const name = this.newCostCenterName().trim();
        if (!name || !this.orgId) {
            this.addCostCenterError.set(this.translate.instant('organizationView.addCostCenter.enterName'));
            return;
        }
        this.addCostCenterError.set(null);
        this.addCostCenterSubmitting.set(true);
        const req: CreateOrgCostCenterRequest = {
            organizationId: this.orgId,
            name,
            description: this.newCostCenterDescription().trim() || undefined
        };
        this.orgCostCenterService.orgCostCentersPost(req).subscribe({
            next: () => {
                this.addCostCenterSubmitting.set(false);
                this.showAddCostCenterForm.set(false);
                this.successMessage.set(this.translate.instant('organizationView.success.costCenterAdded'));
                this.loadCostCenters();
            },
            error: (error: any) => {
                this.addCostCenterError.set(error.error?.title || this.translate.instant('organizationView.errors.addCostCenter'));
                this.addCostCenterSubmitting.set(false);
            }
        });
    }

    confirmDeleteCostCenter(costCenterId: string): void {
        this.deleteCostCenterConfirmId.set(costCenterId);
    }

    cancelDeleteCostCenter(): void {
        this.deleteCostCenterConfirmId.set(null);
    }

    deleteCostCenter(costCenterId: string): void {
        this.orgCostCenterService.orgCostCentersCostCenterIdDelete(costCenterId).subscribe({
            next: () => {
                this.deleteCostCenterConfirmId.set(null);
                this.successMessage.set(this.translate.instant('organizationView.success.costCenterRemoved'));
                this.loadCostCenters();
            },
            error: (error: any) => {
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.removeCostCenter'));
                this.deleteCostCenterConfirmId.set(null);
            }
        });
    }

    // ── Easypass Invitations ─────────────────────────────────────────────────

    loadEasypasses(): void {
        if (!this.orgId) return;
        this.easypassesLoading.set(true);
        this.easypassesError.set(null);
        this.parkingTypeService.parkingTypesEasypassesFilterPost({
            organizationId: this.orgId,
            pageIndex: this.ezPageIndex(),
            pageSize: this.ezPageSize(),
            includeDeleted: false,
            sortBy: 'CreatedAt',
            sortOrder: SortOrder.Descending
        }).subscribe({
            next: (result) => {
                this.easypasses.set(result.items ?? []);
                this.easypassesTotalCount.set(result.totalCount ?? 0);
                this.easypassesLoading.set(false);
                this.easypassesLoaded.set(true);
            },
            error: (error: any) => {
                console.error('Error loading Easypass invitations:', error);
                this.easypassesError.set(error.error?.title || this.translate.instant('organizationView.errors.loadEasypasses'));
                this.easypassesLoading.set(false);
                this.easypassesLoaded.set(true);
            }
        });
    }

    onEasypassesPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.ezPageIndex.set(event.pageIndex);
        this.ezPageSize.set(event.pageSize);
        this.loadEasypasses();
    }

    get easypassesPagination(): JJamesPaginationConfig {
        const total = this.easypassesTotalCount();
        const size = this.ezPageSize();
        return {
            currentPage: this.ezPageIndex(),
            totalPages: Math.max(1, Math.ceil(total / size)),
            pageSize: size,
            totalItems: total,
            itemsPerPage: size,
            pageSizeOptions: [10, 20, 50]
        };
    }

    /** "3/10" for a limited invitation, "5 (unlimited)" for one with no cap. */
    easypassCapacityLabel(pass: EasypassDto): string {
        if (pass.isUnlimited) return `${pass.redemptionCount ?? 0} (${this.translate.instant('organizationView.easypasses.unlimited')})`;
        return `${pass.redemptionCount ?? 0}/${pass.maxRedemptions ?? 0}`;
    }

    easypassStatus(pass: EasypassDto): { label: string; classes: string } {
        if (pass.isConsumed) return { label: this.translate.instant('organizationView.easypasses.statusFull'), classes: 'bg-slate-100 text-slate-600' };
        if (pass.validTo && new Date(pass.validTo) < new Date()) {
            return { label: this.translate.instant('organizationView.easypasses.statusExpired'), classes: 'bg-amber-50 text-amber-700' };
        }
        return { label: this.translate.instant('organizationView.easypasses.statusOpen'), classes: 'bg-emerald-50 text-emerald-700' };
    }

    /** Direct link to this invitation's redeem page in the parker app. */
    easypassRedeemUrl(pass: EasypassDto): string {
        return pass.token ? `${environment.parkerUrl}/easypass/redeem/${pass.token}` : '';
    }

    confirmRevokeEasypass(easypassId: string): void {
        this.revokeEasypassConfirmId.set(easypassId);
    }

    cancelRevokeEasypass(): void {
        this.revokeEasypassConfirmId.set(null);
    }

    revokeEasypass(easypassId: string): void {
        if (!this.orgId) return;
        this.parkingTypeService.parkingTypesEasypassesEasypassIdDelete(easypassId, { organizationId: this.orgId }).subscribe({
            next: () => {
                this.revokeEasypassConfirmId.set(null);
                this.successMessage.set(this.translate.instant('organizationView.success.easypassRevoked'));
                this.loadEasypasses();
            },
            error: (error: any) => {
                this.errorMessage.set(error.error?.title || this.translate.instant('organizationView.errors.revokeEasypass'));
                this.revokeEasypassConfirmId.set(null);
            }
        });
    }

    getPoolConfigCount(): number {
        return this.organization()?.poolParkConfigs?.length ?? 0;
    }

    // ── Transactions workspace ────────────────────────────────────────────────

    /** Defaults the range to the current calendar month, in the operator's own timezone. */
    private initTransactionFilter(): void {
        const now = new Date();
        this.txFrom.set(toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)));
        this.txTo.set(toDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
    }

    /** The org's pool configs — the only handle a parking process gives us on an organization. */
    private orgPoolConfigIds(): string[] {
        return (this.organization()?.poolParkConfigs ?? [])
            .map(c => c.id)
            .filter((id): id is string => !!id);
    }

    loadTransactions(): void {
        const poolIds = this.orgPoolConfigIds();

        // No pools means no stays can exist for this org. Sending an empty list would be read as
        // "these zero pools" and return nothing anyway, but skipping the round trip lets the empty
        // state say why rather than showing a bare "no results".
        if (poolIds.length === 0) {
            this.transactions.set([]);
            this.txTotalCount.set(0);
            this.transactionsLoaded.set(true);
            return;
        }

        this.transactionsLoading.set(true);
        this.transactionsError.set(null);

        this.parkingProcessService.parkingProcessFilterEnrichedPost({
            poolParkingConfigIds: poolIds,
            startedFrom: this.txFrom() ? new Date(this.txFrom()).toISOString() : undefined,
            // End of the chosen day, not its midnight — otherwise the last day of the range is
            // silently excluded and an operator filtering "to the 30th" loses the 30th.
            startedTo: this.txTo() ? new Date(this.txTo() + 'T23:59:59').toISOString() : undefined,
            pageIndex: this.txPageIndex(),
            pageSize: this.txPageSize(),
            includeDeleted: false,
            includeValidity: false
        }).subscribe({
            next: (result) => {
                this.transactions.set(result.items ?? []);
                this.txTotalCount.set(result.totalCount ?? 0);
                this.transactionsLoading.set(false);
                this.transactionsLoaded.set(true);
                this.resolveTransactionSpaceNames(result.items ?? []);
            },
            error: (err: any) => {
                this.transactionsError.set(err?.error?.detail || err?.error?.title || this.translate.instant('organizationView.errors.loadTransactions'));
                this.transactionsLoading.set(false);
                this.transactionsLoaded.set(true);
            }
        });
    }

    applyTransactionFilter(): void {
        this.txPageIndex.set(0);
        this.loadTransactions();
        this.loadTransactionTimeline();
    }

    /**
     * The chart's own read of the whole window. Separate from the table because the table is paged
     * server-side and an occupancy curve drawn from one page is simply wrong.
     */
    loadTransactionTimeline(): void {
        const poolIds = this.orgPoolConfigIds();
        if (poolIds.length === 0) {
            this.txTimelineEvents.set([]);
            this.txTimelineTruncated.set(false);
            return;
        }

        this.parkingProcessService.parkingProcessFilterEnrichedPost({
            poolParkingConfigIds: poolIds,
            startedFrom: this.txFrom() ? new Date(this.txFrom()).toISOString() : undefined,
            startedTo: this.txTo() ? new Date(this.txTo() + 'T23:59:59').toISOString() : undefined,
            pageIndex: 0,
            pageSize: OrgViewComponent.TimelineFetchCap,
            includeDeleted: false,
            includeValidity: false
        }).subscribe({
            next: (result) => {
                const items = result.items ?? [];
                this.txTimelineEvents.set(items.map(t => ({
                    id: t.id ?? '',
                    plate: t.licensePlateDetails?.fullNumber ?? undefined,
                    startedAt: t.startedAt ?? '',
                    endedAt: t.endedAt ?? null,
                    // A pool-full stay carries only the fallback id; the chart draws these apart so an
                    // operator can see which cars were outside the capacity they actually paid for.
                    isOverflow: !!t.fallbackFromPoolConfigId
                })).filter(e => !!e.startedAt));
                this.txTimelineTruncated.set((result.totalCount ?? 0) > OrgViewComponent.TimelineFetchCap);
            },
            // The table is the source of truth for this tab; a failed chart read empties the chart
            // rather than surfacing a second error banner over a working list.
            error: () => {
                this.txTimelineEvents.set([]);
                this.txTimelineTruncated.set(false);
            }
        });
    }

    /** Tops up the space-name map from the ids present in the given rows, fetching only new ones. */
    private resolveTransactionSpaceNames(items: ParkingProcessListItemDto[]): void {
        const known = this.txSpaceNames();
        const missing = [...new Set(
            items.map(t => t.parkingSpaceId).filter((id): id is string => !!id && !known.has(id))
        )];
        if (missing.length === 0) return;

        forkJoin(missing.map(id =>
            this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(id).pipe(catchError(() => of(null)))
        )).subscribe(results => {
            const map = new Map(known);
            results.forEach((dto, i) => map.set(missing[i], dto?.name ?? missing[i]));
            this.txSpaceNames.set(map);
        });
    }

    /** "Space - Zone" for a stay, falling back to whichever half is known. */
    transactionLocation(t: ParkingProcessListItemDto): string {
        const space = t.parkingSpaceId ? this.txSpaceNames().get(t.parkingSpaceId) : null;
        const zone = t.parkingZoneDetails?.name;
        if (space && zone) return `${space} - ${zone}`;
        return space ?? zone ?? '—';
    }

    onTransactionsPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.txPageIndex.set(event.pageIndex);
        this.txPageSize.set(event.pageSize);
        this.loadTransactions();
    }

    get transactionsPagination(): JJamesPaginationConfig {
        const total = this.txTotalCount();
        const size = this.txPageSize();
        return {
            currentPage: this.txPageIndex(),
            totalPages: Math.max(1, Math.ceil(total / size)),
            pageSize: size,
            totalItems: total,
            itemsPerPage: size,
            pageSizeOptions: [10, 20, 50]
        };
    }

    /** Which pool a stay belongs to — including an overflow stay, which carries only the fallback. */
    transactionPoolName(t: ParkingProcessListItemDto): string {
        const id = t.poolParkingConfigId ?? t.fallbackFromPoolConfigId;
        if (!id) return '—';
        const cfg = (this.organization()?.poolParkConfigs ?? []).find(c => c.id === id);
        const name = cfg?.name ?? '—';
        return t.poolParkingConfigId ? name : `${name} (${this.translate.instant('organizationView.transactions.overflow')})`;
    }

    getParkerCount(): number {
        return this.organization()?.parkers?.length ?? 0;
    }

    getZoneCount(): number {
        return this.organization()?.parkingZones?.length ?? 0;
    }

    getSpaceCount(): number {
        return this.organization()?.parkingSpaces?.length ?? 0;
    }
}
